import { useDisplacementMap } from "./DisplacementMapContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { Download, Layers, Move, Shapes, Upload } from "lucide-react";
import {
    NOISE_DISPLACEMENT_STYLE_OPTIONS,
    type ChannelMode,
    type NoiseDisplacementStyle,
} from "../../utils/displacementMap";

export function DisplacementMapControls() {
    const {
        svg,
        svgError,
        svgInputRef,
        handleSvgLoad,
        viewBoxAttr,
        mode,
        setMode,
        seed,
        noiseScale,
        octaves,
        noiseStyle,
        setSeed,
        setNoiseScale,
        setOctaves,
        setNoiseStyle,
        mapImage,
        mapImageError,
        mapImageInputRef,
        handleMapImageLoad,
        channelMode,
        setChannelMode,
        strength,
        circleSegments,
        setStrength,
        setCircleSegments,
        canExport,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useDisplacementMap();

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    return (
        <div className="panel-controls displacement-map-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Displacement Map</h2>
                    <ToolDescriptionPopover dialogLabel="Displacement Map tool description">
                        <p>
                            Upload an SVG and warp its geometry continuously —
                            each vertex moves by an offset from Perlin noise or
                            a raster map image.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="displacement-map"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="SVG to warp" icon={Upload}>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => svgInputRef.current?.click()}
                        >
                            Choose SVG file…
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleSvgLoad}
                        />
                    </div>
                    {svg && (
                        <p className="upload-info upload-info--tight-top">
                            <strong>{svg.name}</strong>
                            {viewBoxAttr && (
                                <>
                                    {" "}
                                    — viewBox: <code>{viewBoxAttr}</code>
                                </>
                            )}
                        </p>
                    )}
                    {!svg && (
                        <p className="upload-hint">
                            Paths, polygons, circles, and rects are all warped.
                        </p>
                    )}
                    {svgError && <p className="upload-error">{svgError}</p>}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Displacement source" icon={Layers}>
                    <div className="displacement-mode-toggle">
                        {(["noise", "image"] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                className={[
                                    "zoom-button",
                                    mode === m ? "zoom-button--active" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onClick={() => setMode(m)}
                            >
                                {m === "noise" ? "Perlin noise" : "Image"}
                            </button>
                        ))}
                    </div>

                    {mode === "noise" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Path warp style
                                    <select
                                        value={noiseStyle}
                                        onChange={(e) =>
                                            setNoiseStyle(
                                                e.target
                                                    .value as NoiseDisplacementStyle,
                                            )
                                        }
                                    >
                                        {NOISE_DISPLACEMENT_STYLE_OPTIONS.map(
                                            (o) => (
                                                <option
                                                    key={o.value}
                                                    value={o.value}
                                                >
                                                    {o.label}
                                                </option>
                                            ),
                                        )}
                                    </select>
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Seed
                                    <DebouncedInput
                                        type="text"
                                        value={seed}
                                        onChange={(v) => setSeed(String(v))}
                                        debounceMs={400}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Noise scale
                                    <DebouncedInput
                                        type="number"
                                        min={0.0001}
                                        max={0.5}
                                        step={0.0005}
                                        value={noiseScale}
                                        onChange={handleFloat(
                                            setNoiseScale,
                                            0.0001,
                                            0.5,
                                        )}
                                        debounceMs={400}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    FBM octaves
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        max={8}
                                        step={1}
                                        value={octaves}
                                        onChange={handleInt(setOctaves, 1)}
                                        debounceMs={300}
                                    />
                                </label>
                            </div>
                        </>
                    )}

                    {mode === "image" && (
                        <>
                            <div className="upload-controls">
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={() =>
                                        mapImageInputRef.current?.click()
                                    }
                                >
                                    Choose map image…
                                </button>
                                <input
                                    ref={mapImageInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="upload-input"
                                    onChange={handleMapImageLoad}
                                />
                            </div>
                            {mapImage && (
                                <p className="upload-info upload-info--tight-top">
                                    {mapImage.width} × {mapImage.height} px
                                </p>
                            )}
                            {!mapImage && !mapImageError && (
                                <p className="upload-hint upload-info--tight-top">
                                    PNG/JPG/WebP accepted. Red → X offset,
                                    Green → Y offset. Mid-gray (128) = no
                                    displacement.
                                </p>
                            )}
                            {mapImageError && (
                                <p className="upload-error">{mapImageError}</p>
                            )}

                            <div className="field-row column field-row-channel-mode">
                                <label className="field-label">
                                    Channel mode
                                </label>
                                <div className="channel-mode-buttons">
                                    {(
                                        [
                                            ["rg", "R→X / G→Y"],
                                            ["luminance", "Luminance"],
                                        ] as [ChannelMode, string][]
                                    ).map(([val, label]) => (
                                        <button
                                            key={val}
                                            type="button"
                                            className={[
                                                "zoom-button",
                                                channelMode === val
                                                    ? "zoom-button--active"
                                                    : "",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                            onClick={() =>
                                                setChannelMode(val)
                                            }
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <p className="control-panel-hint">
                                    {channelMode === "rg"
                                        ? "Designed displacement maps use red for X, green for Y. Grayscale images displace diagonally."
                                        : "Grayscale luminance drives both axes in opposite directions, creating a swirling effect."}
                                </p>
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Displacement" icon={Move}>
                    <div className="field-row">
                        <label className="field-label">
                            Strength (SVG units)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={10000}
                                step={1}
                                value={strength}
                                onChange={handleFloat(setStrength, 0, 10000)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Shape options" icon={Shapes}>
                    <div className="field-row">
                        <label className="field-label">
                            Circle / ellipse segments
                            <DebouncedInput
                                type="number"
                                min={8}
                                max={128}
                                step={4}
                                value={circleSegments}
                                onChange={handleInt(setCircleSegments, 8)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <p className="control-panel-hint">
                        Circles and ellipses are approximated as polygons. More
                        segments = smoother curves.
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Export" icon={Download}>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
